import { useMutation, useQuery } from '@tanstack/react-query'
import { httpClient } from '@/api/http-client'
import { MutationOptions, queryClient } from '@/api/react-query'
import { customerApplicationsQueryKey } from '@/api/financial/customer/list-customer-applications'
import {
  CashflowItem,
  applicationQueryKey,
} from '@/api/financial/application/read-application'

export type PersonalLoanPreview = {
  summary: {
    requested: number
    financed: number
    iof_total: number
    total_to_pay: number
    monthly_rate: number
    cet_monthly: number
    phases: {
      installments: number
      value: number
      grace: number
      first_due: string
    }[]
  }
  cashflow: CashflowItem[]
  limits: {
    min: number
    max: number
    step: number
    min_installments: number
    max_installments: number
    installments_step: number
    monthly_rate: number
    due_dates: string[]
  } | null
  registration_fee: number
  first_due_date: string
}

export type PersonalLoanInput = {
  amount: number
  installments: number
  first_due_date?: string
  crm?: string | null
}

export function usePersonalLoanPreview(input: PersonalLoanInput | null) {
  return useQuery({
    queryKey: ['personal-loan-preview', input],
    queryFn: async () => {
      const response = await httpClient('financial').post<PersonalLoanPreview>(
        '/v1/simulation/personal_loan',
        input,
      )
      return response.data
    },
    enabled: Boolean(input && input.amount > 0 && input.installments > 0),
    placeholderData: (previous) => previous,
    retry: false,
  })
}

export function useSavePersonalLoanSimulation(
  applicationKey: string,
  options?: MutationOptions<PersonalLoanPreview, PersonalLoanInput>,
) {
  return useMutation({
    mutationFn: async (body: PersonalLoanInput) => {
      const response = await httpClient('financial').post<PersonalLoanPreview>(
        `/v1/application/${applicationKey}/simulation`,
        body,
      )
      return response.data
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: applicationQueryKey(applicationKey),
      })
      queryClient.invalidateQueries({ queryKey: customerApplicationsQueryKey })
      options?.onSuccess?.(...args)
    },
  })
}
